import { useRef } from 'react';
import type { Person } from '@atlassian/jira-filters/src/common/types.tsx';
import type { Issue } from '../../../common/types';
import { useIssues } from '../../../services/core-data/main.tsx';
import { useLoggedInUser } from '../../../services/get-current-user';

type Assignees = Map<string, Person>;

/**
 * This hook returns all assignees we've encountered so far, including those from
 * previous filter configurations. If we didn't do this, assignees would disappear
 * from the filter dropdown when a new filter is applied!
 */
export const useAllKnownAssignees = (): Person[] => {
	const { current: allKnownAssignees } = useRef<Assignees>(new Map());
	const { data: issuesResult = [] } = useIssues();
	const currentUser = useLoggedInUser();

	const filteredAssignees =
		currentUser != null
			? issuesResult.filter(({ assignee }) => assignee != null && assignee.id !== currentUser.id)
			: issuesResult;

	filteredAssignees.forEach(({ assignee }: Issue) => {
		if (assignee && !allKnownAssignees.has(assignee.id)) {
			allKnownAssignees.set(assignee.id, assignee);
		}
	});

	if (currentUser != null) {
		return [currentUser, ...allKnownAssignees.values()];
	}

	return [...allKnownAssignees.values()];
};
